import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { db } from "../firebase";
import {
  collection, onSnapshot, query, orderBy,
  addDoc, deleteDoc, doc, serverTimestamp,
} from "firebase/firestore";

const QUIZZES_COL = "baptism_quizzes";
const G = "#c9a55a";
const NAVY = "#12122a";

export default function BaptismQuizLobby() {
  const [quizzes, setQuizzes] = useState([]);
  const [adminUnlocked] = useState(() => localStorage.getItem("baptism_admin_token") === "true");
  const [newQuiz, setNewQuiz] = useState({ title: "", emoji: "🎯", description: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }
    const unsub = onSnapshot(
      query(collection(db, QUIZZES_COL), orderBy("order", "asc")),
      (snap) => setQuizzes(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, []);

  const createQuiz = async () => {
    if (!newQuiz.title.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, QUIZZES_COL), {
        title: newQuiz.title.trim(),
        emoji: newQuiz.emoji || "🎯",
        description: newQuiz.description.trim(),
        active: true,
        order: quizzes.length + 1,
        createdAt: serverTimestamp(),
      });
      setNewQuiz({ title: "", emoji: "🎯", description: "" });
    } finally {
      setSaving(false);
    }
  };

  const deleteQuiz = (id) => deleteDoc(doc(db, QUIZZES_COL, id));

  return (
    <div
      style={{
        background: "linear-gradient(160deg,#0d0d1e 0%,#12122a 60%,#0a0a18 100%)",
        minHeight: "100vh",
        fontFamily: "'Cormorant Garamond',Georgia,serif",
      }}
    >
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <p style={{ letterSpacing: "0.28em", color: `${G}90` }} className="text-xs uppercase mb-3">
            Baptism of Munachimso Zoe Maxwell
          </p>
          <h1 style={{ fontFamily: "'Playfair Display',serif", color: G, lineHeight: 1.1 }} className="text-5xl sm:text-6xl font-bold mb-3">
            Quiz Games
          </h1>
          <p style={{ color: "#6b6b8a" }} className="text-sm italic mb-8">
            Pick a game and show what you know
          </p>
          <div className="flex items-center justify-center gap-3 max-w-xs mx-auto">
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${G}40)` }} />
            <span style={{ color: G }}>✦</span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${G}40,transparent)` }} />
          </div>
        </div>

        {/* Quiz cards */}
        <div className="space-y-4 mb-10">
          {quizzes.length === 0 ? (
            <div className="text-center py-16">
              <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-base">
                No quizzes yet. Ask the host to add one!
              </p>
            </div>
          ) : (
            quizzes.map((quiz, i) => (
              <div
                key={quiz.id}
                onClick={() => navigate(`/baptism/quiz/${quiz.id}`)}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${G}30`,
                  cursor: "pointer",
                }}
                className="rounded-2xl p-6 flex items-center gap-5 hover:bg-white/[0.07] hover:border-amber-400/40 transition-all group"
              >
                {/* Number badge */}
                <div
                  style={{
                    background: `${G}18`,
                    border: `1px solid ${G}40`,
                    color: G,
                    fontFamily: "'Playfair Display',serif",
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                  }}
                  className="rounded-full flex items-center justify-center font-bold text-sm"
                >
                  {i + 1}
                </div>

                {/* Emoji */}
                <div className="text-4xl shrink-0">{quiz.emoji}</div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h2
                    style={{ color: "#f1f5f9", fontFamily: "'Playfair Display',serif" }}
                    className="text-xl font-semibold leading-tight mb-1"
                  >
                    {quiz.title}
                  </h2>
                  {quiz.description && (
                    <p style={{ color: "#6b6b8a" }} className="text-sm">
                      {quiz.description}
                    </p>
                  )}
                </div>

                {/* Arrow */}
                <div
                  style={{ color: G }}
                  className="text-2xl shrink-0 group-hover:translate-x-1 transition-transform"
                >
                  →
                </div>
              </div>
            ))
          )}
        </div>

        {/* Leaderboard link */}
        <div className="text-center mb-16">
          <Link
            to="/baptism/leaderboard"
            style={{ color: `${G}80`, border: `1px solid ${G}30` }}
            className="inline-block text-sm px-6 py-2.5 rounded-full hover:opacity-80 transition-opacity"
          >
            🏆 View Leaderboard
          </Link>
        </div>

        {adminUnlocked && (
          <div className="pb-4">
          {true && (
            <div className="mt-6 text-left">
              <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }} className="rounded-2xl p-6 space-y-5">
                <h3 style={{ color: G, fontFamily: "'Playfair Display',serif" }} className="text-lg font-semibold">
                  Manage Quizzes
                </h3>

                {/* Existing quizzes */}
                {quizzes.length > 0 && (
                  <div className="space-y-4">
                    {quizzes.map((quiz) => {
                      const quizUrl = `${window.location.origin}/baptism/quiz/${quiz.id}`;
                      return (
                        <div key={quiz.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }} className="rounded-2xl p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="text-xl shrink-0">{quiz.emoji}</span>
                            <p style={{ color: "#e2e8f0" }} className="flex-1 text-sm font-medium truncate">{quiz.title}</p>
                            <button
                              onClick={() => navigate(`/baptism/quiz/${quiz.id}`)}
                              style={{ color: G }}
                              className="text-xs hover:opacity-70 transition-opacity shrink-0"
                            >
                              Edit →
                            </button>
                            <button onClick={() => deleteQuiz(quiz.id)} className="text-red-400 hover:text-red-600 text-lg leading-none shrink-0 transition-colors">
                              ×
                            </button>
                          </div>
                          {/* QR code per quiz */}
                          <div className="flex items-center gap-4">
                            <div style={{ background: "#fff", borderRadius: 10, padding: 10, flexShrink: 0 }}>
                              <QRCodeSVG value={quizUrl} size={80} bgColor="#ffffff" fgColor="#12122a" level="M" />
                            </div>
                            <div className="min-w-0">
                              <p style={{ color: "#6b6b8a" }} className="text-xs mb-1">Scan to join</p>
                              <p style={{ color: "rgba(255,255,255,0.3)", wordBreak: "break-all" }} className="text-xs font-mono leading-relaxed">{quizUrl}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Create new quiz */}
                <div style={{ border: "1px solid rgba(255,255,255,0.1)" }} className="rounded-xl p-5 space-y-3">
                  <p style={{ color: "#6b6b8a", letterSpacing: "0.1em" }} className="text-xs uppercase">New Quiz</p>
                  <div className="flex gap-2">
                    <input
                      type="text" placeholder="😊" value={newQuiz.emoji} maxLength={2}
                      onChange={(e) => setNewQuiz((p) => ({ ...p, emoji: e.target.value }))}
                      style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#e2e8f0", textAlign: "center", fontSize: "1.25rem", width: 56 }}
                      className="border rounded-xl py-2 focus:outline-none shrink-0"
                    />
                    <input
                      type="text" placeholder="Quiz title…" value={newQuiz.title}
                      onChange={(e) => setNewQuiz((p) => ({ ...p, title: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && createQuiz()}
                      style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#e2e8f0", fontFamily: "'Cormorant Garamond',serif" }}
                      className="border rounded-xl px-3 py-2 text-sm flex-1 focus:outline-none"
                    />
                  </div>
                  <input
                    type="text" placeholder="Short description (optional)…" value={newQuiz.description}
                    onChange={(e) => setNewQuiz((p) => ({ ...p, description: e.target.value }))}
                    style={{ borderColor: "rgba(255,255,255,0.15)", background: "rgba(255,255,255,0.05)", color: "#e2e8f0", fontFamily: "'Cormorant Garamond',serif" }}
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none"
                  />
                  <button
                    onClick={createQuiz}
                    disabled={saving || !newQuiz.title.trim()}
                    style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)` }}
                    className="w-full text-white font-semibold py-3 rounded-xl text-sm disabled:opacity-40 hover:opacity-90 transition-opacity"
                  >
                    {saving ? "Creating…" : "Create Quiz"}
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
    </div>
  );
}
